/**
 * Model source normalizer (local-model-lifecycle-design.md, "one panel, three types"):
 * everything a user can paste — an ollama tag, an hf.co GGUF ref, a full HuggingFace URL, a
 * copied `ollama pull ...` install command, or an API `provider/model` name — normalizes to ONE
 * typed source. Pure string work: pasted install commands are PARSED for their reference and
 * NEVER executed as shell; unknown forms are rejected with the reason.
 */

import { resolvePrismLocalModelDescriptor } from "./local-execution-planner.ts";

export type ModelSource =
	| { type: "api"; ref: string }
	| { type: "local"; pullRef: string }
	| { type: "transformers"; modelId: string; ref: string }
	| { type: "prism-llamacpp"; modelId: string; ref: string }
	| { type: "needle"; ref: string }
	| { type: "rejected"; reason: string };

const OLLAMA_TAG = /^[a-z0-9][a-z0-9._-]*(?::[A-Za-z0-9._-]+)?$/;
const HF_REF = /^hf\.co\/([\w.-]+)\/([\w.-]+)(?::([\w.-]+))?$/i;
const HF_URL = /^https?:\/\/(?:www\.)?huggingface\.co\/([\w.-]+)\/([\w.-]+)(?:\/.*)?$/i;
const SHELL_METACHARS = /[;&|`$<>(){}\\]/;
const PI_MANAGED_TRANSFORMERS_MODEL_IDS = new Set(["openbmb/MiniCPM5-1B"]);
/**
 * Curated prism-ml models route through the managed Prism llama.cpp adapter instead of Ollama's
 * generic pull path. The execution planner owns the exact artifact and case-insensitive identity;
 * a user-provided quant suffix is accepted as input but cannot override that validated artifact.
 */
/**
 * The single curated needle model id (see needle-runtime.ts) — a standalone function-call test
 * bench with no GGUF/quant concept at all (a pip-installed Python package + one pinned pickle
 * checkpoint), so unlike the prism route above, a quant suffix on the input is NOT accepted — it
 * falls through to the ordinary local/Ollama route instead, mirroring the transformers route's
 * `!quant` gate. Only this exact repo routes to "needle"; any other Cactus-Compute/* ref keeps the
 * ordinary local route.
 */
const NEEDLE_MODEL_ID = "Cactus-Compute/needle";

function normalizeHuggingFaceReference(org: string, repo: string, quant?: string): ModelSource {
	const modelId = `${org}/${repo}`;
	const prismDescriptor = resolvePrismLocalModelDescriptor(modelId);
	if (prismDescriptor) {
		return {
			type: "prism-llamacpp",
			modelId: prismDescriptor.repo,
			ref: `hf.co/${prismDescriptor.repo}`,
		};
	}
	if (!quant && modelId.toLowerCase() === NEEDLE_MODEL_ID.toLowerCase()) {
		return { type: "needle", ref: `hf.co/${NEEDLE_MODEL_ID}` };
	}
	if (!quant && PI_MANAGED_TRANSFORMERS_MODEL_IDS.has(modelId)) {
		return { type: "transformers", modelId, ref: `hf.co/${modelId}` };
	}
	return { type: "local", pullRef: `hf.co/${modelId}${quant ? `:${quant}` : ""}` };
}

export function normalizeModelSource(rawInput: string): ModelSource {
	const input = rawInput.trim();
	if (input.length === 0) return { type: "rejected", reason: "empty input" };
	if (input.length > 500) return { type: "rejected", reason: "input too long to be a model reference" };

	// Pasted install command: extract the reference, never execute anything.
	const installCommand = /^ollama\s+(?:pull|run)\s+(.+)$/i.exec(input);
	if (installCommand) {
		const argument = installCommand[1]!.trim().split(/\s+/)[0] ?? "";
		if (SHELL_METACHARS.test(argument)) {
			return { type: "rejected", reason: "install command argument contains shell metacharacters" };
		}
		const inner = normalizeModelSource(argument);
		if (inner.type === "local") return inner;
		return { type: "rejected", reason: `could not extract a model reference from the install command` };
	}

	if (SHELL_METACHARS.test(input) || /\s/.test(input)) {
		return { type: "rejected", reason: "not a recognized model reference (contains spaces or shell characters)" };
	}

	// Full HuggingFace URL -> either a pi-managed Transformers model (curated full-base ids) or an
	// Ollama hf.co pull ref (GGUF/quant refs still belong to Ollama and keep their :quant suffix).
	const hfUrl = HF_URL.exec(input);
	if (hfUrl) {
		return normalizeHuggingFaceReference(hfUrl[1]!, hfUrl[2]!);
	}

	// hf.co/org/repo[:quant]
	const hfRef = HF_REF.exec(input);
	if (hfRef) {
		return normalizeHuggingFaceReference(hfRef[1]!, hfRef[2]!, hfRef[3]);
	}

	if (input.includes("://")) {
		return { type: "rejected", reason: "only huggingface.co URLs are recognized as local model links" };
	}

	// provider/model -> API-registered model (nothing to install; auth + selection only).
	if (input.includes("/")) {
		const [provider, ...rest] = input.split("/");
		const model = rest.join("/");
		if (provider && model && !model.includes("/")) {
			return { type: "api", ref: `${provider}/${model}` };
		}
		return { type: "rejected", reason: "expected provider/model or hf.co/org/repo[:quant]" };
	}

	// Bare ollama tag ("qwen3:1.7b", "pi-lifter:latest", "llama3").
	if (OLLAMA_TAG.test(input)) {
		return { type: "local", pullRef: input };
	}

	return { type: "rejected", reason: "not a recognized model reference" };
}

/**
 * Whether an installed-model listing entry (`installedName`, as `OllamaRuntime.list()` reports it)
 * is the same model as `ref` (a user-typed reference, or a `models.json`-registered id). Ollama
 * always reports installed models with an explicit tag — a pull with no tag is normalized to
 * `:latest` server-side — but a `ref` is frequently bare ("llama3"), so an exact string compare
 * alone misses that pairing. Exact match always counts; otherwise treat a bare ref against its
 * `:latest`-tagged listing as the same model, and the reverse (a `ref` explicitly pinned to
 * `:latest` against a bare listing), since the local-runtime interface is kept runtime-agnostic
 * (see local-runtime.ts's module docstring) and a future/alternate runtime could list bare names.
 * Shared by every ref-vs-installed-list comparison — see `AgentSession._warnIfManualModelChoiceIsRisky`
 * and `removeLocalModel` in interactive-mode.ts, which had the identical bug independently.
 */
export function matchesInstalledLocalModel(ref: string, installedName: string): boolean {
	if (ref === installedName) return true;
	const hasTag = (value: string): boolean => value.includes(":");
	if (!hasTag(ref) && installedName === `${ref}:latest`) return true;
	if (!hasTag(installedName) && ref === `${installedName}:latest`) return true;
	return false;
}
